import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from '../utils/api';

const AdminReviewManager = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [addressedFilter, setAddressedFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [replyDrafts, setReplyDrafts] = useState({});

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (addressedFilter === 'addressed') params.set('addressed', 'true');
      if (addressedFilter === 'unaddressed') params.set('addressed', 'false');
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);

      const response = await axios.get(`/api/reviews/admin${params.toString() ? `?${params.toString()}` : ''}`);
      setReviews(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const total = reviews.length;
    const addressed = reviews.filter((r) => r.addressed).length;
    const published = reviews.filter((r) => r.publish).length;
    return { total, addressed, unaddressed: total - addressed, published };
  }, [reviews]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'short',
      hour12: true,
    }).format(d);
  };

  const handleAdminUpdate = async (review, payload) => {
    try {
      setSavingId(review.id);
      setError('');
      setSuccess('');
      await axios.put(`/api/reviews/${review.id}/admin`, payload);
      setSuccess('Review updated successfully');
      await loadReviews();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update review');
    } finally {
      setSavingId('');
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-1">Review Management</h2>
          <p className="text-muted mb-0">Moderate buyer feedback, publish/unpublish, reply, and mark addressed.</p>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Row className="g-3 mb-3">
        <Col xs={6} md={3}><Card body className="text-center"><div className="small text-muted">Total</div><div className="fs-4 fw-bold">{stats.total}</div></Card></Col>
        <Col xs={6} md={3}><Card body className="text-center"><div className="small text-muted">Addressed</div><div className="fs-4 fw-bold text-success">{stats.addressed}</div></Card></Col>
        <Col xs={6} md={3}><Card body className="text-center"><div className="small text-muted">Unaddressed</div><div className="fs-4 fw-bold text-warning">{stats.unaddressed}</div></Card></Col>
        <Col xs={6} md={3}><Card body className="text-center"><div className="small text-muted">Published</div><div className="fs-4 fw-bold text-primary">{stats.published}</div></Card></Col>
      </Row>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="small fw-semibold">Addressed Status</Form.Label>
              <Form.Select value={addressedFilter} onChange={(e) => setAddressedFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="addressed">Addressed</option>
                <option value="unaddressed">Unaddressed</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold">From Date</Form.Label>
              <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold">To Date</Form.Label>
              <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Col>
            <Col md={3} className="d-flex gap-2">
              <Button variant="primary" onClick={loadReviews}>Apply</Button>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setAddressedFilter('all');
                  setStartDate('');
                  setEndDate('');
                  setTimeout(loadReviews, 0);
                }}
              >
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : reviews.length === 0 ? (
        <Alert variant="info">No reviews found for the selected filters.</Alert>
      ) : (
        <Card>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>User</th>
                  <th>Rating</th>
                  <th>Comment</th>
                  <th>Publish</th>
                  <th>Addressed</th>
                  <th>Review Date</th>
                  <th>Admin Reply</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review) => (
                  <tr key={review.id}>
                    <td>{review.product_name || review.product_id}</td>
                    <td>
                      <div>{review.user_name || 'User'}</div>
                      <small className="text-muted">{review.user_id}</small>
                    </td>
                    <td>{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</td>
                    <td style={{ minWidth: 200 }}>{review.comment || <span className="text-muted">No comment</span>}</td>
                    <td>{review.publish ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</td>
                    <td>{review.addressed ? <Badge bg="success">Yes</Badge> : <Badge bg="warning">No</Badge>}</td>
                    <td>{formatDateTime(review.created_at)}</td>
                    <td style={{ minWidth: 250 }}>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={Object.prototype.hasOwnProperty.call(replyDrafts, review.id) ? replyDrafts[review.id] : (review.admin_reply || '')}
                        onChange={(e) => setReplyDrafts((prev) => ({ ...prev, [review.id]: e.target.value }))}
                        placeholder="Write admin reply"
                      />
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-2" style={{ minWidth: 180 }}>
                        <Button
                          size="sm"
                          variant={review.publish ? 'outline-secondary' : 'outline-primary'}
                          disabled={savingId === review.id}
                          onClick={() => handleAdminUpdate(review, { publish: !review.publish })}
                        >
                          {review.publish ? 'Unpublish' : 'Publish'}
                        </Button>
                        <Button
                          size="sm"
                          variant={review.addressed ? 'outline-warning' : 'outline-success'}
                          disabled={savingId === review.id}
                          onClick={() => handleAdminUpdate(review, { addressed: !review.addressed })}
                        >
                          {review.addressed ? 'Mark Unaddressed' : 'Mark Addressed'}
                        </Button>
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={savingId === review.id}
                          onClick={() => handleAdminUpdate(review, { admin_reply: (replyDrafts[review.id] ?? review.admin_reply ?? '').trim() })}
                        >
                          {savingId === review.id ? 'Saving...' : 'Save Reply'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default AdminReviewManager;
